import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';
import { formatDate } from '../../../utils/jour';

const STATUT_COLORS = { actif: '#2e7d32', repos: '#f57c00', retraite: '#9e9e9e' };
const STATUT_LABELS = { actif: 'Actif', repos: 'Repos', retraite: 'Retraite' };
const SANTE_COLORS = { bon: '#2e7d32', a_verifier: '#f57c00', traitement: '#e53935' };
const SANTE_LABELS = { bon: 'Bon', a_verifier: 'A verifier', traitement: 'En traitement' };

// ⓘ `statut_sante` n'y figure plus : il est DEDUIT cote serveur, jamais saisi.
const EMPTY = {
  nom: '', race: '', date_naissance: '', maitre_chien_id: '', statut: 'actif',
  derniere_visite_veto: '', prochaine_visite_veto: '', notes: '',
};


export default function CynophilePage() {
  const [tab, setTab] = useState('chiens');
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEscapeClose(showForm, () => setShowForm(false));

  /* ⚠️ CET ECRAN MASQUAIT SES ECHECS (corrige le 03/09/2026).
   *    `api.get('/cynophile').catch(() => ({ data: [] }))` transformait un 404
   *    en LISTE VIDE : la route n'existait pas, l'utilisateur saisissait, rien
   *    ne s'enregistrait, et RIEN NE LE PREVENAIT.
   *    ➡️ CINQUIEME OCCURRENCE du repli silencieux. Une erreur se DIT. */
  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [chiensRes, agentsRes] = await Promise.all([
        api.get('/cynophile'),
        api.get('/agents').catch(() => ({ data: [] })),
      ]);
      setItems(chiensRes.data);
      setAgents(agentsRes.data);
    } catch (err) {
      setItems([]);
      const code = err.response?.status;
      setError(code === 404
        ? "Le module cynophile n'est pas encore disponible sur ce serveur : rien de ce qui est saisi ici ne serait enregistre. Ne rien saisir tant que ce message s'affiche."
        : (err.response?.data?.error || "Les chiens n'ont pas pu etre lus. Rien n'est affiche plutot qu'une liste vide trompeuse."));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm(EMPTY); setError(''); setShowForm(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      nom: c.nom || '', race: c.race || '', date_naissance: c.date_naissance?.split('T')[0] || '',
      maitre_chien_id: c.maitre_chien_id || '', statut: c.statut || 'actif',
      derniere_visite_veto: c.derniere_visite_veto?.split('T')[0] || '',
      prochaine_visite_veto: c.prochaine_visite_veto?.split('T')[0] || '', notes: c.notes || '',
    });
    setError(''); setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (editId) await api.put(`/cynophile/${editId}`, form);
      else await api.post('/cynophile', form);
      setShowForm(false); setForm(EMPTY); setEditId(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce chien ?')) return;
    // ⚠️ Le `catch {}` d'origine avalait l'echec : l'utilisateur croyait avoir
    //    supprime. On DIT ce qui s'est passe.
    try { await api.delete(`/cynophile/${id}`); load(); }
    catch (err) { setError(err.response?.data?.error || 'La suppression a echoue.'); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const total = items.length;
  const actifs = items.filter(c => c.statut === 'actif').length;
  const santeOk = items.filter(c => c.statut_sante === 'bon').length;
  const aVerifier = items.filter(c => c.statut_sante === 'a_verifier').length;

  const statCards = [
    { label: 'Total chiens', value: total, color: '#1565c0' },
    { label: 'Chiens actifs', value: actifs, color: '#2e7d32' },
    { label: 'Sante OK', value: santeOk, color: '#00897b' },
    { label: 'Sante a verifier', value: aVerifier, color: '#f57c00' },
  ];

  const agentName = (id) => {
    const a = agents.find(a => a.id === id);
    return a ? `${a.prenom} ${a.nom}` : '-';
  };

  return (
    <div>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {statCards.map(sc => (
          <div key={sc.label} className="content-card" style={{ padding: '1.2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: sc.color }}>{sc.value}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--gss-text-light)', marginTop: '0.25rem' }}>{sc.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { id: 'chiens', label: 'Chiens' },
          { id: 'sante', label: 'Sante' },
          { id: 'formations', label: 'Formations' },
          { id: 'affectations', label: 'Affectations' },
        ].map(t => (
          <button key={t.id} className={`gss-btn ${tab === t.id ? 'gss-btn-primary' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <button className="gss-btn gss-btn-primary" onClick={openCreate}>+ Ajouter un chien</button>
      </div>

      {loading ? <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p> : (
        <>
          {tab === 'chiens' && (
            <div className="content-card">
              <div className="content-card-title">Liste des chiens</div>
              {items.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
                    <path d="M10 5.172C10 3.782 8.423 2.679 6.5 3c-2.823.47-4.113 6.006-4 7 .137 1.217 1.2 1.5 2 1.5s1.409-.497 2-1.5c.747-1.266 1.5-1.5 2-1.5s1 .5 1 1v7.5c0 .828-.5 1.5-1 2s-1 1.172-1 2S8.172 22 9 22h6"/>
                  </svg>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucun enregistrement</div>
                  <div style={{ fontSize: 14, color: '#999' }}>Cliquez sur "+ Ajouter un chien" pour commencer</div>
                </div>
              ) : (
                <div className="gss-table-wrap">
                  <table className="gss-table">
                    <thead><tr><th>Nom</th><th>Race</th><th>Maitre-chien</th><th>Statut sante</th><th>Derniere visite veto</th><th>Prochaine visite</th><th>Statut</th><th style={{ width: 90 }}>Actions</th></tr></thead>
                    <tbody>
                      {items.map(c => (
                        <tr key={c.id}>
                          <td><strong>{c.nom}</strong></td>
                          <td>{c.race || '-'}</td>
                          <td>{agentName(c.maitre_chien_id)}</td>
                          <td><span className="gss-badge" style={{ background: (SANTE_COLORS[c.statut_sante] || '#999') + '20', color: SANTE_COLORS[c.statut_sante] || '#999' }}>{SANTE_LABELS[c.statut_sante] || c.statut_sante}</span></td>
                          <td>{formatDate(c.derniere_visite_veto)}</td>
                          <td style={{ color: c.prochaine_visite_veto && new Date(c.prochaine_visite_veto) < new Date() ? '#e53935' : 'inherit', fontWeight: c.prochaine_visite_veto && new Date(c.prochaine_visite_veto) < new Date() ? 600 : 'normal' }}>{formatDate(c.prochaine_visite_veto)}</td>
                          <td><span className="gss-badge" style={{ background: (STATUT_COLORS[c.statut] || '#999') + '20', color: STATUT_COLORS[c.statut] || '#999' }}>{STATUT_LABELS[c.statut] || c.statut}</span></td>
                          <td>
                            <div className="gss-actions">
                              <button className="gss-btn-icon" onClick={() => openEdit(c)} title="Modifier"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                              <button className="gss-btn-icon gss-btn-icon--delete" onClick={() => handleDelete(c.id)} title="Supprimer"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'sante' && (
            <div className="content-card">
              <div className="content-card-title">Suivi sante</div>
              {items.length === 0 ? (
                <p style={{ color: 'var(--gss-text-light)' }}>Aucun enregistrement</p>
              ) : (
                <div className="gss-table-wrap">
                  <table className="gss-table">
                    <thead><tr><th>Nom</th><th>Statut sante</th><th>Derniere visite</th><th>Prochaine visite</th><th>Notes</th></tr></thead>
                    <tbody>
                      {items.map(c => (
                        <tr key={c.id}>
                          <td><strong>{c.nom}</strong></td>
                          <td><span className="gss-badge" style={{ background: (SANTE_COLORS[c.statut_sante] || '#999') + '20', color: SANTE_COLORS[c.statut_sante] || '#999' }}>{SANTE_LABELS[c.statut_sante] || c.statut_sante}</span></td>
                          <td>{formatDate(c.derniere_visite_veto)}</td>
                          <td>{formatDate(c.prochaine_visite_veto)}</td>
                          <td style={{ fontSize: '0.85rem', color: 'var(--gss-text-secondary)' }}>{c.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'formations' && (
            <div className="content-card">
              <div className="content-card-title">Formations maitre-chien</div>
              {agents.filter(a => a.statut === 'actif').length === 0 ? (
                <p style={{ color: 'var(--gss-text-light)' }}>Aucun agent enregistre</p>
              ) : (
                <div className="gss-table-wrap">
                  <table className="gss-table">
                    <thead><tr><th>Matricule</th><th>Agent</th><th>Poste</th><th>Formation maitre-chien</th></tr></thead>
                    <tbody>
                      {agents.filter(a => a.statut === 'actif').slice(0, 20).map(a => (
                        <tr key={a.id}>
                          <td><strong>{a.matricule}</strong></td>
                          <td>{a.prenom} {a.nom}</td>
                          <td style={{ textTransform: 'capitalize' }}>{a.poste?.replace(/_/g, ' ') || '-'}</td>
                          <td><span className="gss-badge gss-badge-grey">Non renseignee</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'affectations' && (
            <div className="content-card">
              <div className="content-card-title">Affectations chien / maitre-chien</div>
              {items.length === 0 ? (
                <p style={{ color: 'var(--gss-text-light)' }}>Aucun enregistrement</p>
              ) : (
                <div className="gss-table-wrap">
                  <table className="gss-table">
                    <thead><tr><th>Chien</th><th>Race</th><th>Maitre-chien assigne</th><th>Statut</th></tr></thead>
                    <tbody>
                      {items.map(c => (
                        <tr key={c.id}>
                          <td><strong>{c.nom}</strong></td>
                          <td>{c.race || '-'}</td>
                          <td>{agentName(c.maitre_chien_id)}</td>
                          <td><span className="gss-badge" style={{ background: (STATUT_COLORS[c.statut] || '#999') + '20', color: STATUT_COLORS[c.statut] || '#999' }}>{STATUT_LABELS[c.statut] || c.statut}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                <h2>{editId ? 'Modifier le chien' : 'Ajouter un chien'}</h2>
              </div>
              <div className="gss-modal-subtitle">Equipe canine de securite</div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            {error && <div style={{ padding: '0.5rem 1.5rem', color: '#e53935', fontSize: '0.9rem' }}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span>IDENTIFICATION</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Nom du chien *</label>
                    <input className="gss-input" required value={form.nom} onChange={e => setField('nom', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Race</label>
                    <input className="gss-input" value={form.race} onChange={e => setField('race', e.target.value)} placeholder="Berger allemand, Malinois..." />
                  </div>
                  <div className="gss-field">
                    <label>Date de naissance</label>
                    <input className="gss-input" type="date" value={form.date_naissance} onChange={e => setField('date_naissance', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Maitre-chien assigne</label>
                    <select className="gss-input" value={form.maitre_chien_id} onChange={e => setField('maitre_chien_id', e.target.value)}>
                      <option value="">-- Selectionner --</option>
                      {agents.filter(a => a.statut === 'actif').map(a => (
                        <option key={a.id} value={a.id}>{a.matricule} — {a.prenom} {a.nom}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                  </svg>
                  <span>STATUT & SANTE</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Statut</label>
                    {/* Les 5 valeurs que la base accepte — l'ecran n'en offrait
                        que 3 : « malade » et « decede » etaient inatteignables. */}
                    <select className="gss-input" value={form.statut} onChange={e => setField('statut', e.target.value)}>
                      <option value="actif">Actif</option>
                      <option value="repos">Repos</option>
                      <option value="malade">Malade</option>
                      <option value="retraite">Retraite</option>
                      <option value="decede">Decede</option>
                    </select>
                  </div>
                  {/* ⚠️ LE STATUT SANTE NE SE SAISIT PAS, IL SE DEDUIT :
                      « En traitement » si le chien est marque malade,
                      « A verifier » si un rappel veterinaire est depasse,
                      « Bon » sinon.
                      Le champ etait un menu deroulant que RIEN n'enregistrait —
                      un champ non cable ne s'expose pas a la saisie. */}
                  <div className="gss-field">
                    <label>Statut sante</label>
                    <div className="gss-input" style={{ display: 'flex', alignItems: 'center',
                      background: 'var(--gss-bg)', color: 'var(--gss-text-light)', fontSize: '0.85rem' }}>
                      Deduit du statut du chien et des rappels veterinaires
                    </div>
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  <span>SUIVI VETERINAIRE</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Derniere visite veto</label>
                    <input className="gss-input" type="date" value={form.derniere_visite_veto} onChange={e => setField('derniere_visite_veto', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Prochaine visite veto</label>
                    <input className="gss-input" type="date" value={form.prochaine_visite_veto} onChange={e => setField('prochaine_visite_veto', e.target.value)} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Notes</label>
                    <textarea className="gss-input" rows="3" value={form.notes} onChange={e => setField('notes', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>{saving ? 'Enregistrement...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg>{editId ? 'Modifier' : 'Ajouter'}</>}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
